import React, { useEffect, useState, useRef, useMemo } from "react";
import "./GoogleReviews.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faStarHalfAlt,
  faStar as faRegStar,
} from "@fortawesome/free-solid-svg-icons";
import StickyHeadline from "./StickyHeadline";
import { Headline, Paragraph, Icon, Section, ContentBlock } from "./ui";

const GoogleReviews = ({
  darkMode,
  stickyHeadline = null,
  navbarHeight = 160,
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  const [itemsPerView, setItemsPerView] = useState(3);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const autoRotateRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      // Use Strapi API for cached reviews
      const strapiUrl =
        process.env.REACT_APP_STRAPI_URL ||
        "https://strapi.staging.wtm-sportwagen.com";
      const url = `${strapiUrl}/api/google-reviews`;

      try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.data && data.data.length > 0) {
          // Transform Strapi data format to match component expectations
          const transformedData = {
            reviews: data.data.map((item) => ({
              author_name: item.attributes.authorName,
              profile_photo_url: item.attributes.authorPhotoUrl,
              rating: item.attributes.rating,
              text: item.attributes.text,
              relative_time_description:
                item.attributes.relativeTimeDescription,
              time: item.attributes.time,
            })),
            // Use business metadata from Strapi
            rating: data.meta?.overallRating || 4.8,
            user_ratings_total: data.meta?.totalReviews || 25,
          };
          setReviews(transformedData);
        } else {
          setError("No reviews found for this place.");
        }
      } catch (error) {
        setError("Error fetching reviews");
        console.error("Strapi reviews fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobileView(width < 768);

      // Set items per view based on screen size
      if (width >= 1200) {
        setItemsPerView(3);
      } else if (width >= 768) {
        setItemsPerView(2);
      } else {
        setItemsPerView(1);
      }
    };

    handleResize(); // Call once on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-rotation with infinite scroll behavior
  useEffect(() => {
    if (!isAutoRotating || !reviews.reviews || reviews.reviews.length === 0)
      return;

    const startAutoRotation = () => {
      autoRotateRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const originalLength = reviews.reviews.length;

          // Don't scroll if we don't have enough reviews
          if (originalLength <= itemsPerView) {
            return 0;
          }

          const nextIndex = prevIndex + 1;

          // Infinite scroll: Reset when we reach the position showing duplicate content
          if (nextIndex === originalLength) {
            setTimeout(() => {
              setIsTransitioning(false);
              setCurrentIndex(0);
              setTimeout(() => {
                setIsTransitioning(true);
              }, 50);
            }, 1200);
          }

          return nextIndex;
        });
      }, 3500); // Rotate every 3.5 seconds
    };

    startAutoRotation();

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
    };
  }, [isAutoRotating, reviews.reviews, itemsPerView]);

  // Pause auto-rotation when user interacts
  const pauseAutoRotation = () => {
    setIsAutoRotating(false);
    if (autoRotateRef.current) {
      clearInterval(autoRotateRef.current);
    }
  };

  // Resume auto-rotation after user stops interacting
  const resumeAutoRotation = () => {
    setIsAutoRotating(true);
  };

  // Create infinite scrolling array by duplicating first items at the end
  const cyclicalReviews = useMemo(() => {
    if (!reviews.reviews || reviews.reviews.length === 0) return [];
    const originalReviews = reviews.reviews;

    // Duplicate first items at end for seamless infinite scrolling
    const duplicateCount = Math.min(itemsPerView, originalReviews.length);
    const duplicatedReviews = originalReviews.slice(0, duplicateCount);
    const result = [...originalReviews, ...duplicatedReviews];

    return result;
  }, [reviews.reviews, itemsPerView]);

  if (loading)
    return (
      <Paragraph color="secondary" className="opacity-75">
        Loading reviews...
      </Paragraph>
    );
  if (error) return <Paragraph className="text-red-400">{error}</Paragraph>;

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div style={{ marginBottom: 10 }}>
        {[...Array(fullStars)].map((_, index) => (
          <FontAwesomeIcon
            key={`full-${index}`}
            icon={faStar}
            style={{ color: "#F7BB0A" }}
          />
        ))}
        {hasHalfStar && (
          <FontAwesomeIcon icon={faStarHalfAlt} style={{ color: "#F7BB0A" }} />
        )}
        {[...Array(emptyStars)].map((_, index) => (
          <FontAwesomeIcon
            key={`empty-${index}`}
            icon={faRegStar}
            style={{ color: "gray" }}
          />
        ))}
      </div>
    );
  };

  // Get actual computed dimensions to prevent drift
  const getTransform = () => {
    // Get the first review card to measure actual dimensions
    const firstCard = document.querySelector(".review");
    if (!firstCard) {
      // Fallback if card not found
      return `translateX(-${currentIndex * 416}px)`;
    }

    // Get actual computed dimensions
    const cardRect = firstCard.getBoundingClientRect();
    const cardWidth = cardRect.width;

    // Get actual gap from computed styles
    const container = document.querySelector(".reviews-container");
    const computedStyle = container ? getComputedStyle(container) : null;
    const gap = computedStyle ? parseFloat(computedStyle.gap) : 16;

    const moveDistance = cardWidth + gap;
    return `translateX(-${currentIndex * moveDistance}px)`;
  };

  return (
    <div className="relative">
      {/* Sticky Headline - Full Width */}
      {stickyHeadline && (
        <StickyHeadline headline={stickyHeadline} navbarHeight={navbarHeight} />
      )}

      <Section variant="primary" spacing="comfortable" margin={false}>
        <ContentBlock maxWidth="4xl" className="mx-auto">
          <Headline
            variant="h2"
            className="text-left mb-12 sm:mb-16"
            color="secondary"
            noSpacing
          >
            {/* Das sagen unsere Kunden auf Google */}
          </Headline>
        </ContentBlock>

        <div
          className="carousel-container"
          onMouseEnter={pauseAutoRotation}
          onMouseLeave={resumeAutoRotation}
        >
          <div
            className="reviews-container gap-3 md:gap-4"
            style={{
              transform: getTransform(),
              transition: isTransitioning
                ? "transform 1.2s cubic-bezier(0.23, 1, 0.32, 1)"
                : "none",
            }}
          >
            {cyclicalReviews.map((review, index) => (
              <div
                key={`review-${index}-${
                  index >= reviews.reviews.length ? "duplicate" : "original"
                }`}
                className="review rounded-sm"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/google-icon.svg`}
                  alt="google-icon"
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    width: "32px",
                    padding: "5px",
                  }}
                />
                <div className="review-header">
                  {review.profile_photo_url ? (
                    <img
                      src={review.profile_photo_url}
                      alt={review.author_name}
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src =
                          "https://via.placeholder.com/40/CCCCCC/666666?text=" +
                          (review.author_name
                            ? review.author_name.charAt(0).toUpperCase()
                            : "?");
                      }}
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <img
                      src={
                        "https://via.placeholder.com/40/CCCCCC/666666?text=" +
                        (review.author_name
                          ? review.author_name.charAt(0).toUpperCase()
                          : "?")
                      }
                      alt="Default Avatar"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <div className="review-name-wrapper">
                    <Headline variant="h6" color="secondary">
                      {review.author_name}
                    </Headline>
                    <Paragraph
                      variant="tiny"
                      color="secondary"
                      className="opacity-75"
                    >
                      {review.relative_time_description}
                    </Paragraph>
                  </div>
                </div>
                {renderStars(review.rating)}
                <div className="review-text-container">
                  <Paragraph
                    variant="small"
                    color="secondary"
                    className="review-text"
                  >
                    {review.text}
                  </Paragraph>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Google My Business Link */}
        {/* <div className="text-center mt-8 mb-6">
        <a
          href="https://www.google.com/maps/place/WTM+Sportwagen+Werkstatt"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-6 py-3 bg-white hover:bg-gray-100 text-gray-800 font-semibold rounded-lg transition duration-200 shadow-md hover:shadow-lg"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Alle Bewertungen auf Google ansehen
          <Icon name="external-link" size="sm" className="ml-2" />
        </a>
      </div> */}

        <ContentBlock maxWidth="4xl" className="mx-auto">
          <div className="rating-summary">
            <Paragraph
              variant="small"
              color="secondary"
              noSpacing
              className="text-center mt-5 opacity-75"
            >
              <strong>Google</strong> Gesamtbewertung{" "}
              <strong>{reviews.rating}</strong> von 5, basierend auf{" "}
              <strong>{reviews.user_ratings_total} Bewertungen</strong>
            </Paragraph>
          </div>
        </ContentBlock>
      </Section>
    </div>
  );
};

export default GoogleReviews;
